import React, { useMemo, useCallback, useState, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import TooltipV2 from "@/components/UI/Tooltip";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { SearchableLightbox } from "@/components/DataFieldInputSelector/SearchableLightbox";
import { TitleInputWithDefault } from "../../utils/TitleInputWithDefault";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { useLineChartConfig } from "./ConfigForm_Context";
import { DATA_KEYS } from "./DATA_KEYS";
import { CollapseTransition, CollapseTransitionItem } from "../../utils/CollapseTransition";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";
import { normalizeDisplayValue } from "../../utils/filterHiddenDatapoints";
import { getHiddenForPresetFromTimeSeries } from "../../WidgetBarChart/utils/getTopBottomDatapoints";

export const ConfigForm_BreakdownSection = () => {
  const {
    widgetConfig,
    setConfig,
    underEditWidgetIdx,
    dataKeys,
    dimensionOptions,
    arithmeticState,
  } = useLineChartConfig();

  const {
    dataFields,
    sortedLetters,
    numberInputOptions,
    hasMultipleFields,
    aggregateType,
    aggregateOptions,
    customExpression,
    validationError,
    handleFieldAChange,
    handleFieldChange,
    handleRemoveField,
    handleAggregateChange,
    handleCustomExpressionChange,
  } = arithmeticState;

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[widgetConfig.id];

  // Build visibility options from time-series dimensions. Skip datapoints with no
  // caption (empty/null display) — they'd otherwise show as a blank row in the list.
  const visibilityOptions = useMemo(() => {
    if (!data?.length || !dataFields.A) return [];
    const set = new Set();
    data.forEach(timePoint => {
      timePoint.dimensions?.forEach(dim => {
        const nd = normalizeDisplayValue(dim.display);
        if (nd !== "") set.add(nd);
      });
    });
    return Array.from(set).map(d => ({ display: d, value: d }));
  }, [data, dataFields.A]);

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  const visibilityModifier = useVisibilityModifier({
    dimension: 1,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput: false,
    updateConfig,
    keyOverrides: {
      hiddenKey: DATA_KEYS.hiddenDimDatapoints,
      presetKey: DATA_KEYS.dimVisibilityPreset,
      nKey: DATA_KEYS.dimVisibilityN,
    },
    optionsOverride: visibilityOptions,
    getHiddenForPreset: getHiddenForPresetFromTimeSeries,
  });

  const aggregateValue = useMemo(() => {
    const match = aggregateOptions.find(o => o.value === aggregateType);
    if (!match) return null;
    return { ...match, dataType: "Aggregate" };
  }, [aggregateOptions, aggregateType]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {/* Field rows */}
      <AnimatePresence initial={false}>
        {sortedLetters.map((letter) => {
          const field = (
            <DataFieldInputSelector
              key={letter}
              indicator={sortedLetters.length > 1 ? { label: letter } : undefined}
              options={letter === 'A' ? dimensionOptions : numberInputOptions}
              value={dataFields[letter]}
              onChange={(id, v) => {
                if (letter === 'A') {
                  handleFieldAChange(v);
                } else {
                  handleFieldChange(letter, v);
                }
              }}
              actions={
                sortedLetters.length > 1
                  ? [{ icon: 'X', onClick: () => handleRemoveField(letter), hoverColor: 'red' }]
                  : dataFields.A
                    ? [{ icon: 'X', onClick: () => handleFieldAChange(null), hoverColor: 'red' }]
                    : []
              }
              placeholder="Select field"
              modifier={letter === 'A' ? visibilityModifier : undefined}
              picker={getPickerProp({
                widgetComp: 'WidgetLineChart',
                slot: letter === 'A' ? 'breakdown' : 'numberFields',
              })}
            />
          );
          if (letter === 'A') return field;
          return (
            <CollapseTransitionItem key={letter} id={letter}>
              {field}
            </CollapseTransitionItem>
          );
        })}
      </AnimatePresence>

      {/* Aggregate section - only show if multiple fields */}
      <CollapseTransition isVisible={hasMultipleFields}>
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <DataFieldInputSelector
            id={`${dataKeys.dimension}-aggregate`}
            options={aggregateOptions}
            value={aggregateValue}
            onChange={handleAggregateChange}
            placeholder="Select Aggregate"
            placeholderIcon="MathOperations"
            trailingIcon="CaretRight"
          />

          <CollapseTransition isVisible={aggregateType === 'custom'}>
            <TooltipV2 text="Enter a custom expression for the aggregation" placement="right" wrapperDisplay="block">
              <TitleInputWithDefault
                id={`${dataKeys.dimension}-custom-expr`}
                value={customExpression}
                onChange={handleCustomExpressionChange}
                placeholder="e.g., (A + B) - C"
                tagLabel="EXPRESSION"
                validationStatus={validationError ? { type: 'error', msg: validationError } : null}
              />
            </TooltipV2>
          </CollapseTransition>
        </div>
      </CollapseTransition>
    </div>
  );
};

/** Exposed for ConfigForm/index.js to place in SectionWrapper headerRight */
export const BreakdownAddFieldButton = () => {
  const { arithmeticState } = useLineChartConfig();
  const { canAddFields, handleAddField, numberInputOptions, dataFields } = arithmeticState;
  const [showLightbox, setShowLightbox] = useState(false);
  const btnRef = useRef(null);

  // Values already used by fields A, B, C... — so we don't offer them again.
  const addedValues = useMemo(() => {
    const set = new Set();
    Object.values(dataFields).forEach((f) => {
      if (f?.value != null) set.add(f.value);
    });
    return set;
  }, [dataFields]);

  // numberInputOptions minus already-added fields. Preserves the flat/grouped shape and
  // drops any group that becomes empty after filtering.
  const availableOptions = useMemo(() => {
    const isGrouped = numberInputOptions.some(item => Array.isArray(item.options));
    if (!isGrouped) {
      return numberInputOptions.filter((o) => !addedValues.has(o.value));
    }
    return numberInputOptions
      .map((group) =>
        Array.isArray(group.options)
          ? { ...group, options: group.options.filter((o) => !addedValues.has(o.value)) }
          : group
      )
      .filter((group) => !Array.isArray(group.options) || group.options.length > 0);
  }, [numberInputOptions, addedValues]);

  const hasAvailableOptions = availableOptions.length > 0;
  const canAdd = canAddFields && hasAvailableOptions;

  return (
    <>
      <TooltipV2
        text={
          !dataFields.A
            ? "Add data to visualize"
            : !canAddFields
              ? "Activates only on number fields for arithmetic aggregation"
              : !hasAvailableOptions
                ? "No other compatible fields are available"
                : "Add additional number fields to include in this calculation"
        }
        placement="top"
      >
        <span ref={btnRef}>
          <ButtonGhost
            icon="Plus"
            onClick={() => setShowLightbox(true)}
            disabled={!canAdd}
            style={{ padding: "4px" }}
          />
        </span>
      </TooltipV2>
      {showLightbox && (
        <SearchableLightbox
          anchorRef={btnRef}
          onClose={() => setShowLightbox(false)}
          options={availableOptions}
          grouped={availableOptions.some(item => item.options && Array.isArray(item.options))}
          value={undefined}
          onChange={(_, selectedField) => {
            let enriched = selectedField;
            if (selectedField && !selectedField.categoryLabel) {
              const group = availableOptions.find(g =>
                Array.isArray(g.options) && g.options.some(o => o.value === selectedField.value)
              );
              if (group) enriched = { ...selectedField, categoryLabel: group.display };
            }
            handleAddField(enriched);
            setShowLightbox(false);
          }}
          searchPlaceholder="Search fields"
        />
      )}
    </>
  );
};
